import React, { useEffect, useCallback, createContext, Suspense, useState } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { isAuthenticated, isLoggedIn, isSessionActive, language, recentLogout } from './redux/reducers/AuthReducer';
import { checkAllowedCapabilities, setUserAbilitiesByState } from './utils/Capability';
import fontAwesomeLib from './config/IconLibrary';
import { Routes as configRouter } from './config/Routes';
import Ability from './utils/Ability';
import { PublicRoutes, PrivateRoutes, SessionRoutes } from './routes';
import { HomeLayout } from './layouts';
import NotFound from './pages/Generic/NotFound';
import { modifyLanguage } from './config/i18n';
import { AbilityContext, GlobalToastContainer, Loading } from './components/common';
import ModalContainer from './components/Modal/ModalContainer';
import Forbidden from './pages/Generic/Forbidden';
import config from './envConfig/config';
import Analytics from './Analytics';

const favIconUrls = config.REACT_APP_FAVICON;
export const BreadcrumbContext = createContext([]);
fontAwesomeLib.init();

const App = ({ isAuthenticated, isSessionActive, isLoggedIn, recentLogout, language, setUserAbilities }) => {
  const [capabilitiesReady, setCapabilitiesReady] = useState(false);

  const updateFavicon = useCallback(() => {
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = favIconUrls;
    document.getElementsByTagName('head')[0].appendChild(link);
  }, []);

  useEffect(() => {
    async function initializeUserAbilities() {
      await setUserAbilities();
      setCapabilitiesReady(true);
    }
    initializeUserAbilities();
  }, [setUserAbilities]);

  useEffect(() => {
    if (language !== null) {
      modifyLanguage(language);
    }
    updateFavicon();
  }, [language, updateFavicon]);

  if (!capabilitiesReady) {
    return <Loading />;
  }

  return (
    <AbilityContext.Provider value={Ability}>
      <Analytics />
      <Router>
        <Suspense fallback={<Loading />}>
          <Switch>
            {PublicRoutes.map(({ Component, path }) => (
              <Route
                exact
                path={path}
                key={`public-routes-${path}`}
                render={(route) => {
                  const {
                    location: { pathname, state: { from } = { from: configRouter.DASHBOARD } },
                  } = route;

                  if (isAuthenticated) {
                    return <Redirect to={recentLogout ? configRouter.DASHBOARD : from} />;
                  }

                  if (path === configRouter.LOGIN_MFA && !isLoggedIn) {
                    return (
                      <Redirect
                        to={{
                          pathname: configRouter.LOGIN,
                          state: { from: pathname },
                        }}
                      />
                    );
                  }

                  return <Component route={route} />;
                }}
              />
            ))}

            {PrivateRoutes.map(({ Component, path, breadcrumbs, capabilities }) => (
              <Route
                exact
                path={path}
                key={`private-routes-${path}`}
                render={(route) => {
                  if (isAuthenticated) {
                    if (checkAllowedCapabilities(capabilities)) {
                      const showFaq = breadcrumbs && breadcrumbs[0] && breadcrumbs[0].showFaq;
                      const faqUrl = breadcrumbs && breadcrumbs[0] && breadcrumbs[0].faqUrl;
                      return (
                        <HomeLayout {...route}>
                          <BreadcrumbContext.Provider value={breadcrumbs} showFaq={showFaq} faqUrl={faqUrl}>
                            <Component route={route} />
                          </BreadcrumbContext.Provider>
                        </HomeLayout>
                      );
                    }
                    return <Redirect to={configRouter.FORBIDDEN} />;
                  }
                  return (
                    <Redirect
                      to={{
                        pathname: configRouter.LOGIN,
                        state: { from: route.location.pathname },
                      }}
                    />
                  );
                }}
              />
            ))}

            {SessionRoutes.map(({ Component, path }) => (
              <Route
                exact
                path={path}
                key={`session-routes-${path}`}
                render={(route) => (isSessionActive ? <Component route={route} /> : <Redirect to={configRouter.UNAUTHORIZED} />)}
              />
            ))}

            <Route exact path={configRouter.FORBIDDEN} component={Forbidden} />
            <Route component={NotFound} />
          </Switch>
          <ModalContainer />
          <GlobalToastContainer />
        </Suspense>
      </Router>
    </AbilityContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
  isSessionActive: isSessionActive(state),
  isLoggedIn: isLoggedIn(state),
  recentLogout: recentLogout(state),
  language: language(state),
  setUserAbilities: () => {
    setUserAbilitiesByState(state);
  },
});

export default connect(mapStateToProps)(App);
